import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ThemeConfig, HospitalBranding } from '../types/theme';
import { defaultTheme, themes } from '../config/themes';
import { readableTextOn, isDarkSurface } from '../utils/themeGenerator';
import { kitVarsFor } from '../config/maieKit';

interface ThemeStore {
  currentTheme: ThemeConfig;
  availableThemes: ThemeConfig[];
  branding: HospitalBranding;

  // Actions
  setTheme: (themeId: string) => void;
  setCustomTheme: (theme: ThemeConfig) => void;
  setBranding: (branding: Partial<HospitalBranding>) => void;
}

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      currentTheme: defaultTheme,
      availableThemes: themes,
      // Initial branding before /system-settings/frontend/hospital-config
      // resolves. Real values arrive via useTheme + themeApi (which now
      // overlays tenant_brandings, so the OWNER's clinic name + colours
      // populate as soon as the post-auth fetch returns).
      // Initial branding shown only until /system-settings/frontend/hospital-config
      // resolves with the tenant's saved branding. We deliberately don't read
      // VITE_HOSPITAL_NAME here — that env was meant for the legacy single-
      // tenant build and let stale values like "MediCare Hospital" leak into
      // PDFs. Real branding always comes from tenant_brandings; if it's empty,
      // the platform default ("MAIE") is the right placeholder.
      branding: {
        hospitalName: 'MAIE',
        tagline: 'Excellence in Healthcare',
        aiProvider: 'MAIE AI'
      },

      setTheme: (themeId) => {
        const theme = get().availableThemes.find(t => t.id === themeId);
        if (theme) {
          set({ currentTheme: theme });
          applyThemeToDOM(theme);
        }
      },

      setCustomTheme: (theme) => {
        set({ currentTheme: theme });
        applyThemeToDOM(theme);
      },

      setBranding: (branding) => {
        // Drop undefined keys before merging. The post-auth /theme-config
        // refetch returns `logo: undefined` when the GET endpoint hasn't
        // mirrored the just-saved tenant_brandings.logoUrl yet — without
        // this filter, every refetch wipes the local logo back to blank.
        // Same hazard for primaryColor/accentColor/favicon.
        const next: Partial<HospitalBranding> = {};
        for (const [k, v] of Object.entries(branding) as [
          keyof HospitalBranding,
          HospitalBranding[keyof HospitalBranding],
        ][]) {
          if (v !== undefined) (next as Record<string, unknown>)[k] = v;
        }
        set((state) => ({
          branding: { ...state.branding, ...next },
        }));
      },
    }),
    {
      name: 'theme-storage',
      // v2: brand palette switched to MAIE indigo (April 2026) — drop stale
      //     `modern-blue` snapshots so the new `--color-primary-*` defaults
      //     from index.css apply.
      // v3: drop the legacy "MediCare Hospital" / VITE_HOSPITAL_NAME
      //     placeholder that got persisted on first boot. Pre-PMF tenants
      //     hit a single shared FE build, so the wrong env-var default
      //     leaked into PDFs. Resetting branding lets the post-auth
      //     /system-settings refetch hydrate the real tenant name.
      // v4: stop persisting `availableThemes`. The catalogue is static code
      //     (config/themes.ts); snapshotting it meant newly-shipped presets
      //     (e.g. the dark themes) stayed invisible to existing users until
      //     they cleared app-data — the "new themes only appeared after a
      //     reset" bug. The catalogue now always comes from code via
      //     `partialize` below; only the viewer's pick + branding persist.
      // v5: STOP persisting `branding`. Branding (logo, favicon, colours,
      //     clinic name) is server-authoritative — it comes from
      //     tenant_brandings via themeApi on every auth mount (staleTime 60s +
      //     refetchOnWindowFocus). Persisting it meant a stale localStorage
      //     copy was shown on boot and "forced" over backend edits (an OWNER
      //     changing the logo/colours wouldn't see it; a cleared field never
      //     cleared). Now only the viewer's resolved palette is cached as a
      //     boot placeholder; branding always re-hydrates from the server.
      version: 5,
      // Persist only the resolved palette — never branding or the catalogue.
      // `availableThemes` is code-sourced (config/themes.ts) so new presets
      // ship immediately; `branding` is server-sourced (see v5 above).
      partialize: (state) => ({
        currentTheme: state.currentTheme,
      }),
      // `partialize` narrows the persisted shape to {currentTheme}, so migrate
      // must return exactly that. Older snapshots' persisted `branding` /
      // `availableThemes` are intentionally dropped (now server/code-sourced).
      migrate: (persistedState, version) => {
        const old = (persistedState ?? {}) as Partial<ThemeStore>;
        return {
          // v2: drop stale `modern-blue` palette snapshots so the new
          // `--color-primary-*` defaults apply. Otherwise keep the saved pick.
          currentTheme:
            version < 2 ? defaultTheme : old.currentTheme ?? defaultTheme,
        };
      },
      onRehydrateStorage: () => (state) => {
        // The persisted snapshot no longer carries `availableThemes`
        // (partialize drops it), so the store keeps the code-sourced
        // catalogue from the initializer. Re-apply the saved palette.
        if (state?.currentTheme) {
          applyThemeToDOM(state.currentTheme);
        }
      }
    }
  )
);

// Helper function to apply theme to DOM
function applyThemeToDOM(theme: ThemeConfig) {
  const root = document.documentElement;
  
  // Apply primary colors
  Object.entries(theme.colors.primary).forEach(([shade, color]) => {
    root.style.setProperty(`--color-primary-${shade}`, color);
  });
  
  // Apply secondary colors
  Object.entries(theme.colors.secondary).forEach(([shade, color]) => {
    root.style.setProperty(`--color-secondary-${shade}`, color);
  });
  
  // Apply other colors
  root.style.setProperty('--color-accent', theme.colors.accent);
  root.style.setProperty('--color-bg-primary', theme.colors.background.primary);
  root.style.setProperty('--color-bg-secondary', theme.colors.background.secondary);
  root.style.setProperty('--color-bg-tertiary', theme.colors.background.tertiary);
  root.style.setProperty('--color-text-primary', theme.colors.text.primary);
  root.style.setProperty('--color-text-secondary', theme.colors.text.secondary);
  root.style.setProperty('--color-text-tertiary', theme.colors.text.tertiary);
  root.style.setProperty('--color-text-button', theme.colors.text.button || '#ffffff');
  root.style.setProperty('--color-border-primary', theme.colors.border.primary);
  root.style.setProperty('--color-border-secondary', theme.colors.border.secondary);
  root.style.setProperty('--color-success', theme.colors.success);
  root.style.setProperty('--color-warning', theme.colors.warning);
  root.style.setProperty('--color-error', theme.colors.error);
  root.style.setProperty('--color-info', theme.colors.info);
  
  // Apply fonts
  root.style.setProperty('--font-primary', theme.font.primary);
  root.style.setProperty('--font-secondary', theme.font.secondary);
  
  // Apply border radius
  Object.entries(theme.borderRadius).forEach(([size, value]) => {
    root.style.setProperty(`--radius-${size}`, value);
  });
  
  // Apply shadows
  Object.entries(theme.shadows).forEach(([size, value]) => {
    root.style.setProperty(`--shadow-${size}`, value);
  });

  // Design-kit properties (--brand-*, --ink-*, --surface-*, --r-*, --gap-*).
  // Ported `_kit` components style against these names rather than --color-*,
  // so they follow the tenant's theme instead of forking per clinic.
  // See config/maieKit.ts.
  Object.entries(kitVarsFor(theme)).forEach(([name, value]) => {
    root.style.setProperty(name, value);
  });

  // Derived interactive-state vars (auto-contrast for selected/hover/fills).
  // The selected/hover TINTS live in index.css keyed off [data-theme-scheme]
  // so they recompute from the primary/text vars we just set above. Here we
  // only (a) flag the scheme — explicit theme.scheme wins, luminance of the
  // page background is the fallback for older themes that predate the field —
  // and (b) pick a readable text colour for a SOLID primary fill (badges,
  // active buttons), which can't be expressed in pure CSS.
  const isDark =
    theme.scheme === 'dark' ||
    (theme.scheme == null && isDarkSurface(theme.colors.background.primary));
  root.dataset.themeScheme = isDark ? 'dark' : 'light';

  // Keep Tailwind's `.dark` in step with the palette we just painted.
  //
  // `.dark` drives the shadcn tokens (--card, --background, --foreground) and
  // is normally toggled by useAccessibilityStore from the colorScheme PREF.
  // The palette, though, comes from the persisted theme — and the two are
  // separate persisted stores, so they can disagree: a saved
  // `currentTheme: periwinkle-dark` alongside `colorScheme: 'light'` paints a
  // dark palette on light chrome, i.e. #e8eaf6 text on a white card. Reachable
  // by toggling dark and then having the pref revert.
  //
  // The palette is what is actually on screen, so the chrome follows it. When
  // pref and theme agree (the normal case) this is a no-op.
  root.classList.toggle('dark', isDark);
  root.style.setProperty(
    '--color-on-primary',
    readableTextOn(theme.colors.primary[600]),
  );
}